import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft,
  Plus,
  Loader2,
  FileText,
  Ship,
  Plane,
} from "lucide-react";
import InvoiceFormCard, {
  invoiceFormSchema,
  type InvoiceFormValues,
} from "@/components/invoicing/InvoiceFormCard";
import TripSection, {
  DEFAULT_TRIP_SECTION,
  type TripSectionValue,
} from "@/components/invoicing/TripSection";
import {
  useAtomicInvoiceSubmit,
  useInvoicesByTrip,
  useTrips,
} from "@/hooks/useInvoices";
import { useCurrentTramsContext } from "@/hooks/useTramsAccounts";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { FINANCES_PATHS } from "@/lib/financesPaths";
import { TramsAccountSwitcher } from "@/components/trams/TramsAccountSwitcher";
import {
  formatTramsInvoiceDisplayNumber,
  getTramsInvoiceNumberFromMetadata,
} from "@shared/invoice-display";

const EMPTY_INVOICE: InvoiceFormValues = {
  travelerFirstName: "",
  travelerLastName: "",
  supplierLinkNo: undefined,
  supplierName: "",
  travelType: "",
  propertyName: "",
  shipName: "",
  cabinCount: undefined,
  tourName: "",
  description: "",
  confirmationNumber: "",
  departureDate: "",
  returnDate: "",
  totalFare: "",
  commissionAmount: "",
  agencyLinkNo: 0,
};

interface InvoiceEntry {
  id: string;
  values: InvoiceFormValues;
  isSubmitted: boolean;
}

function formatInvoiceDate(value: string | Date | null | undefined): string {
  if (!value) return "";
  const str = typeof value === "string" ? value : value.toISOString();
  const datePart = str.substring(0, 10);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "";
  return `${month}/${day}/${year}`;
}

function formatInvoiceMoney(value: string | null | undefined): string {
  const parsed = parseFloat(value ?? "");
  const amount = Number.isNaN(parsed) ? 0 : parsed;
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function draftHasData(values: InvoiceFormValues): boolean {
  return (
    values.supplierName.length > 0 ||
    values.travelerFirstName.length > 0 ||
    values.travelerLastName.length > 0 ||
    values.confirmationNumber.length > 0 ||
    values.totalFare.length > 0
  );
}

export default function CreateInvoice() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const atomicSubmit = useAtomicInvoiceSubmit();
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const { data: tramsData } = useCurrentTramsContext();
  const currentTramsAccountId = tramsData?.context?.tramsAccountId;

  const preselectedTripId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("tripId");
    return raw ? Number(raw) : null;
  }, []);

  const { data: allTrips } = useTrips();

  const [tripSection, setTripSection] = useState<TripSectionValue>(
    DEFAULT_TRIP_SECTION
  );

  const existingTripId =
    tripSection.mode === "existing" ? tripSection.existingTripId ?? undefined : undefined;

  const {
    data: existingInvoices,
    isLoading: existingInvoicesLoading,
    isError: existingInvoicesError,
    error: existingInvoicesFetchError,
  } = useInvoicesByTrip(existingTripId, currentTramsAccountId);

  const loadedExistingInvoices =
    !existingInvoicesLoading && !existingInvoicesError ? (existingInvoices ?? []) : [];

  const [tripPreselected, setTripPreselected] = useState(false);
  const [invoiceEntries, setInvoiceEntries] = useState<InvoiceEntry[]>([
    { id: crypto.randomUUID(), values: { ...EMPTY_INVOICE }, isSubmitted: false },
  ]);
  const [activeInvoiceIndex, setActiveInvoiceIndex] = useState(0);
  useEffect(() => {
    if (tripPreselected || !preselectedTripId || !allTrips) return;
    const trip = allTrips.find((t) => t.id === preselectedTripId);
    if (trip) {
      setTripSection({
        mode: "existing",
        existingTripId: trip.id,
        existingTrip: trip,
        newTrip: DEFAULT_TRIP_SECTION.newTrip,
      });
      setTripPreselected(true);
    }
  }, [preselectedTripId, allTrips, tripPreselected]);

  const currentEntry = invoiceEntries[activeInvoiceIndex];

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: currentEntry?.values ?? EMPTY_INVOICE,
    mode: "onBlur",
  });

  const saveCurrentInvoiceToEntries = useCallback(() => {
    const currentValues = form.getValues();
    setInvoiceEntries((prev) =>
      prev.map((entry, i) =>
        i === activeInvoiceIndex ? { ...entry, values: currentValues } : entry
      )
    );
  }, [form, activeInvoiceIndex]);

  const switchToInvoice = (idx: number) => {
    saveCurrentInvoiceToEntries();
    setActiveInvoiceIndex(idx);
    const entry = invoiceEntries[idx];
    if (entry) {
      form.reset(entry.values);
    }
  };

  const addInvoice = () => {
    saveCurrentInvoiceToEntries();
    const newEntry: InvoiceEntry = {
      id: crypto.randomUUID(),
      values: { ...EMPTY_INVOICE },
      isSubmitted: false,
    };
    setInvoiceEntries((prev) => [...prev, newEntry]);
    setActiveInvoiceIndex(invoiceEntries.length);
    form.reset(EMPTY_INVOICE);
  };

  const removeInvoice = (idx: number) => {
    if (invoiceEntries.length <= 1) return;
    setInvoiceEntries((prev) => prev.filter((_, i) => i !== idx));
    if (activeInvoiceIndex >= idx && activeInvoiceIndex > 0) {
      const newIdx = activeInvoiceIndex - 1;
      setActiveInvoiceIndex(newIdx);
      const entry = invoiceEntries[newIdx === idx ? idx - 1 : newIdx];
      if (entry) form.reset(entry.values);
    } else if (activeInvoiceIndex === idx) {
      const entry = invoiceEntries[0];
      setActiveInvoiceIndex(0);
      if (entry) form.reset(entry.values);
    }
  };

  const hasUnsavedData = () => {
    const currentValues = form.getValues();
    const hasInvoiceData =
      currentValues.travelerFirstName.length > 0 ||
      currentValues.travelerLastName.length > 0 ||
      currentValues.confirmationNumber.length > 0 ||
      currentValues.totalFare.length > 0;
    const hasTripData =
      tripSection.mode === "new"
        ? tripSection.newTrip.name.length > 0
        : tripSection.existingTripId !== null;
    return hasInvoiceData || hasTripData;
  };

  const { requestLeave } = useUnsavedChangesGuard(hasUnsavedData);

  const handleCancel = () => {
    requestLeave(() => setLocation(FINANCES_PATHS.invoices));
  };

  const validateTripSection = (): string | null => {
    if (!tripSection.mode) {
      return "Please select or create a trip before submitting.";
    }
    if (tripSection.mode === "existing" && !tripSection.existingTripId) {
      return "Please select a trip.";
    }
    if (tripSection.mode === "new") {
      if (!tripSection.newTrip.name.trim()) {
        return "Trip name is required.";
      }
      if (tripSection.newTrip.destinations.length === 0) {
        return "At least one destination is required.";
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    saveCurrentInvoiceToEntries();

    if (existingTripId && (existingInvoicesLoading || existingInvoicesError)) {
      toast({
        title: existingInvoicesError ? "Cannot submit yet" : "Loading trip invoices",
        description: existingInvoicesError
          ? "Existing invoices could not be loaded. Refresh the page before submitting."
          : "Please wait while existing invoices finish loading.",
        variant: "destructive",
      });
      return;
    }

    const tripError = validateTripSection();
    if (tripError) {
      toast({ title: "Trip Required", description: tripError, variant: "destructive" });
      return;
    }

    const validatedInvoices: InvoiceFormValues[] = [];
    for (let i = 0; i < invoiceEntries.length; i++) {
      const entry = i === activeInvoiceIndex ? form.getValues() : invoiceEntries[i].values;
      const result = invoiceFormSchema.safeParse(entry);
      if (!result.success) {
        if (i !== activeInvoiceIndex) switchToInvoice(i);
        toast({
          title: `Invoice #${i + 1} has errors`,
          description: result.error.issues.map((e) => e.message).join(", "),
          variant: "destructive",
        });
        form.trigger();
        return;
      }
      validatedInvoices.push(result.data);
    }

    const tripPayload =
      tripSection.mode === "existing"
        ? { mode: "existing" as const, tripId: tripSection.existingTripId! }
        : {
            mode: "new" as const,
            name: tripSection.newTrip.name,
            destinations: tripSection.newTrip.destinations,
            isGroup: tripSection.newTrip.isGroup,
            groupName: tripSection.newTrip.groupName || undefined,
          };

    try {
      const result = await atomicSubmit.mutateAsync({
        trip: tripPayload,
        invoices: validatedInvoices.map((inv) => ({
          ...inv,
          primaryTraveler: `${inv.travelerLastName}/${inv.travelerFirstName}`,
          departureDate: new Date(inv.departureDate).toISOString(),
          returnDate: new Date(inv.returnDate).toISOString(),
          ...(currentTramsAccountId ? { tramsAccountId: currentTramsAccountId } : {}),
        })),
        idempotencyKey: idempotencyKeyRef.current,
      });

      const tripLabel = result.trip?.recordLocator
        ? `Trip ${result.trip.recordLocator}`
        : `Trip #${result.trip?.id ?? ""}`;
      const invoiceNums = (result.invoices ?? [])
        .map((inv: any) =>
          formatTramsInvoiceDisplayNumber(getTramsInvoiceNumberFromMetadata(inv.metadata)),
        )
        .filter((n) => n !== "—")
        .join(", ");

      toast({
        title: "Invoices Created",
        description: `${tripLabel} · Invoice${validatedInvoices.length > 1 ? "s" : ""} ${invoiceNums || validatedInvoices.length} created (sync: Pending).`,
      });
      setLocation(FINANCES_PATHS.invoices);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create invoices. Please try again.",
        variant: "destructive",
      });
    }
  };

  const canSubmit =
    !atomicSubmit.isPending &&
    invoiceEntries.length >= 1 &&
    !(existingTripId && (existingInvoicesLoading || existingInvoicesError));

  const visibleDraftCount = invoiceEntries.filter((entry, idx) => {
    const vals = idx === activeInvoiceIndex ? form.getValues() : entry.values;
    return draftHasData(vals) || invoiceEntries.length > 1;
  }).length;

  const showInvoicesBlock =
    loadedExistingInvoices.length > 0 ||
    visibleDraftCount > 0 ||
    (existingTripId != null && (existingInvoicesLoading || existingInvoicesError));

  const invoicesBlockCount =
    existingInvoicesLoading && existingTripId
      ? "…"
      : loadedExistingInvoices.length + visibleDraftCount;

  return (
    <div className="flex flex-col h-full">
      {/* Top Bar */}
      <div className="flex-shrink-0 border-b bg-background px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 max-w-6xl mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <h1 className="text-xl font-semibold truncate">Create New Invoice</h1>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
            <TramsAccountSwitcher />
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {atomicSubmit.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Trip + Invoice Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              <TripSection value={tripSection} onChange={setTripSection} />

              {showInvoicesBlock && (
                <>
                  <Separator />

                  {/* Invoice List */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between px-1">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        Invoices ({invoicesBlockCount})
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addInvoice}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Another
                      </Button>
                    </div>

                {existingInvoicesLoading && existingTripId && (
                  <div className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading existing invoices...
                  </div>
                )}

                {existingInvoicesError && existingTripId && (
                  <div className="px-1 py-2 text-sm text-destructive">
                    Could not load existing invoices.
                    {existingInvoicesFetchError instanceof Error &&
                      ` ${existingInvoicesFetchError.message}`}
                  </div>
                )}

                {!existingInvoicesLoading &&
                  !existingInvoicesError &&
                  loadedExistingInvoices.map((invoice) => {
                  const departure = formatInvoiceDate(invoice.departureDate);
                  const returnDate = formatInvoiceDate(invoice.returnDate);

                  return (
                    <Card key={`existing-${invoice.id}`} className="bg-muted/30">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {invoice.supplierName ||
                                  `Invoice #${formatTramsInvoiceDisplayNumber(getTramsInvoiceNumberFromMetadata(invoice.metadata))}`}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {invoice.primaryTraveler || "—"}
                                {invoice.totalFare &&
                                  ` · $${formatInvoiceMoney(invoice.totalFare)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <Badge variant="secondary" className="text-xs">
                              Existing
                            </Badge>
                            {departure && returnDate && (
                              <Badge variant="outline" className="text-xs">
                                {departure} – {returnDate}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {loadedExistingInvoices.length > 0 && visibleDraftCount > 0 && (
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1 pt-1">
                    New
                  </p>
                )}

                {invoiceEntries.map((entry, idx) => {
                  const vals =
                    idx === activeInvoiceIndex
                      ? form.getValues()
                      : entry.values;
                  const isActive = idx === activeInvoiceIndex;
                  const hasData = draftHasData(vals);
                  if (invoiceEntries.length === 1 && !hasData) return null;
                  const draftLabel =
                    loadedExistingInvoices.length > 0
                      ? `New Invoice #${idx + 1}`
                      : `Invoice #${idx + 1}`;

                  return (
                    <Card
                      key={entry.id}
                      className={`cursor-pointer transition-colors ${
                        isActive
                          ? "ring-2 ring-primary border-primary"
                          : "hover:border-primary/50"
                      }`}
                      onClick={() => {
                        if (!isActive) switchToInvoice(idx);
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {vals.supplierName || draftLabel}
                              </p>
                              {hasData && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {vals.travelerLastName && vals.travelerFirstName
                                  ? `${vals.travelerLastName}/${vals.travelerFirstName}`
                                  : vals.travelerFirstName || vals.travelerLastName || ''}
                                  {vals.totalFare &&
                                    ` · $${vals.totalFare}`}
                                </p>
                              )}
                            </div>
                          </div>
                          {vals.departureDate && vals.returnDate && (
                            <Badge
                              variant="outline"
                              className="text-xs flex-shrink-0"
                            >
                              {vals.departureDate} – {vals.returnDate}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                  </div>
                </>
              )}
            </div>

            {/* Right Column: Active Invoice Form */}
            <div className="lg:col-span-2">
              <FormProvider {...form}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <InvoiceFormCard
                    form={form}
                    index={activeInvoiceIndex}
                    canRemove={invoiceEntries.length > 1}
                    onRemove={() => removeInvoice(activeInvoiceIndex)}
                  />
                  <div className="flex justify-end gap-3 mt-6">
                    <Button type="button" variant="outline" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={!canSubmit}>
                      {atomicSubmit.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </ScrollArea>

    </div>
  );
}
